import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';

import { AbstractBaseComponent } from '@ib-shared/abstract/abstract-base';
import { isEmptyValue } from '@ib-shared/helpers/is-empty';
import { CountriesHttpService } from '@ib-shared/services/countries-data.service';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs';

interface IOption {
  currencyCode: string;
  currencyTitle: string;
  countryName?: string;
  currencyFullName?: string;
  isMajorCurrency?: boolean;
}

@Component({
  selector: 'ib-currency',
  templateUrl: './currency.component.html',
})
export class CurrencyComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy
{
  @Input() abstractFormCtrl: AbstractControl; // for autocomplete
  @Input() formCtrl: FormControl; // for select
  @Input() initByIp = false;
  @Input() showTitle = true;
  @Input() showPlaceholder = true;
  @Input() parentForm: FormGroup;
  @Input() title: string;
  @Input() type: 'autocomplete' | 'select' = 'select';

  private allCurrencies$ = new BehaviorSubject<Array<IOption>>([]);
  public searchCtrl = new FormControl('');
  public filteredCurrencies$: Observable<Array<IOption>>;
  public isDropdownOpen: boolean;

  get isPanelOpen(): boolean {
    return this.isDropdownOpen;
  }

  get value(): string {
    return this.abstractFormCtrl?.value || this.formCtrl?.value;
  }

  @Output() currencyChanged = new EventEmitter<string>();

  @HostBinding('class') get classes(): string {
    return [
      this.formCtrl?.touched ? 'ib-currency--touched' : null,
      this.formCtrl?.invalid ? 'ib-currency--invalid' : null,
    ]
      .filter(i => i)
      .join(' ');
  }

  constructor(private countriesHttpS: CountriesHttpService) {
    super();
  }

  ngOnInit(): void {
    if (this.type === 'select' && !this.formCtrl) {
      throw new Error(
        'CurrencyComponent: you must add @Input() formCtrl: FormControl',
      );
    }
    if (this.type === 'autocomplete' && !this.abstractFormCtrl) {
      throw new Error(
        'CurrencyComponent: you must add @Input() abstractFormCtrl: AbstractControl',
      );
    }
    this.initCurrencyOptions();
    if (!this.isCurrencyDefined() && this.initByIp) {
      this.initCurrencyByIp();
    }
    this.initAllCountries();
    this.initDefaultSearchName();
  }

  ngOnDestroy(): void {
    super.destroySubscriptions();
  }

  toggle(a: MatAutocompleteTrigger): void {
    this.isDropdownOpen ? a.closePanel() : a.openPanel();
  }

  changed(): void {
    this.currencyChanged?.emit(this.value);
  }

  blur(): void {
    if (isEmptyValue(this.searchCtrl.value)) {
      this.formCtrl.setValue(null);
    }
    this.formCtrl?.markAsTouched();
    this.formCtrl?.markAsDirty();
  }

  private initAllCountries(): void {
    this.countriesHttpS
      .selectAllCountries()
      .pipe(
        filter(r => Boolean(r.length)),
        takeUntil(this.destroy$),
      )
      .subscribe(list => {
        const options: Array<IOption> = [];
        // list
        //   .filter(i => ['usd', 'gbp', 'eur'].includes(i.code))
        //   .map(i => ({
        //     currencyCode: i.currency,
        //     currencyTitle: i.currency,
        //     countryName: i.name,
        //   }));

        const baseCurrencies: Array<IOption> = [
          {
            currencyCode: 'eur',
            currencyTitle: 'EUR',
            currencyFullName: 'EUR', // 'Euro',
            isMajorCurrency: true,
          },
          {
            currencyCode: 'gbp',
            currencyTitle: 'GBP',
            currencyFullName: 'GBP', // 'UK Sterling',
            isMajorCurrency: true,
          },
          {
            currencyCode: 'usd',
            currencyTitle: 'USD',
            currencyFullName: 'USD', // 'US Dollars',
            isMajorCurrency: true,
          },
        ];
        this.allCurrencies$.next([...baseCurrencies, ...options]);
        this.initDefaultSearchName();
      });
  }

  private initDefaultSearchName(): void {
    const init = (currencyCode: string, currencyList: Array<IOption>): void => {
      const data = currencyList?.find(
        c => c.currencyCode?.toLowerCase() === currencyCode?.toLowerCase(),
      );
      this.searchCtrl.setValue(data?.currencyCode);
    };
    init(this.formCtrl.value, this.allCurrencies$.value);
    combineLatest([
      this.formCtrl.valueChanges,
      this.allCurrencies$.asObservable(),
    ])
      .pipe(
        // take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(([currencyCode, currencyList]) => {
        init(currencyCode, currencyList);
      });
  }

  private isCurrencyDefined(): boolean {
    return Boolean(this.formCtrl.value);
  }

  private initCurrencyByIp(): void {
    this.countriesHttpS
      .selectUserCountryByIp()
      .pipe(
        switchMap(dataByIp => {
          return this.countriesHttpS.selectAllCountries().pipe(
            filter(countryList => Boolean(countryList.length)),
            map(countryList => {
              return countryList.find(
                i =>
                  i.currency.toLowerCase() === dataByIp.currency.toLowerCase(),
              );
            }),
          );
        }),
      )
      .subscribe(r => {
        this.searchCtrl.setValue(r.currency);
        this.formCtrl.setValue(r.currency.toLowerCase());
      });
  }

  private initCurrencyOptions(): void {
    this.filteredCurrencies$ = combineLatest([
      this.allCurrencies$,
      this.searchCtrl.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([countries, searchVal]) => {
        // const list = countries?.length
        //   ? countries.filter(i => {
        //       return searchVal
        //         ? i.countryName
        //             ?.toLowerCase()
        //             .startsWith(searchVal.toLowerCase() || '') ||
        //             i.currencyCode
        //               .toLowerCase()
        //               .startsWith(searchVal.toLowerCase())
        //         : i;
        //     })
        //   : [];
        return countries;
      }),
    );
  }

  selectPanelToggled(e: boolean): void {
    this.isDropdownOpen = e;
  }

  selected(v: MatAutocompleteSelectedEvent): void {
    const data = this.allCurrencies$.value?.find(i => {
      return i.currencyCode.toLowerCase() === v.option.value.toLowerCase();
    });
    this.formCtrl.setValue(data?.currencyCode?.toLowerCase());
  }
}
